<template>
  <ap-modal
    v-if="open"
    centered
    class="create-qr-modal"
    :closable="false"
    :title="$t('components.modal.create.project.title')"
    v-model:open="visible"
    destroy-on-close>
    <ap-form ref="formRef" :model="formModel" layout="vertical" :rules="rules" hide-required-mark>
      <ap-form-item :label="$t(field.label)" class="mb-2" :name="field.name">
        <ap-input
          v-model:value="formModel[field.name]"
          size="large"
          :name="field.name"
          :disabled="isLoading"
          :placeholder="$t(field.placeholder)"
          id="titleField" />
      </ap-form-item>
    </ap-form>
    <template #footer>
      <ap-button @click="onCancel" :disabled="isLoading" class="border-black/30 text-primary">
        {{ $t("components.button.cancel.label") }}
      </ap-button>
      <ap-button
        type="primary"
        html-type="submit"
        :disabled="isLoading"
        :loading="isLoading"
        @click="onCreate"
        :class="{ 'bg-primary opacity-50 text-white': isLoading }">
        {{ $t("components.button.create.label") }}
      </ap-button>
    </template>
  </ap-modal>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from "vue"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import CreateProjectHandler from "@/handlers/Project/CreateProjectHandler"
import useToastManager from "@/composables/toastManager"

const visible = defineModel("open", { default: false, type: Boolean })
const router = useRouter()
const i18n = useI18n()
const formRef = ref()
const formModel = reactive({ title: undefined })
const formIsValid = computed(() => Boolean(formModel.title?.length))
const ExistentTitles = ref([])
const isLoading = ref(false)
const field = ref({
  name: "title",
  label: i18n.t("components.modal.create.project.name.label"),
  placeholder: i18n.t("components.modal.create.project.name.hint"),
  type: "text",
  span: 24
})

let rules = ref({
  title: [
    {
      trigger: "change",
      required: true,
      message: i18n.t("component.error.empty")
    }
  ]
})

function onCancel() {
  visible.value = false
  formModel.title = undefined
}

async function onCreate() {
  isLoading.value = true
  try {
    await formRef.value.validate().then(async () => {
      await CreateProjectHandler.apply(formModel.title, router)
      onCancel()
    })
  } catch (error) {
    const violation = error.violations?.length ? error.violations[0].message : null
    const helpText = {
      "constraint.project.name.duplicated": i18n.t("component.alert.name.existing.label")
    }

    if (violation && helpText[violation]) {
      ExistentTitles.value = [...ExistentTitles.value, `${formModel.title}`]

      rules.value.title = [
        ...rules.value.title,
        {
          validator: async (_, value) => {
            if (ExistentTitles.value.includes(value)) throw new Error(helpText[violation])
          }
        }
      ]

      formRef.value.validate()

      return
    }

    formIsValid.value &&
      useToastManager().error({ message: i18n.t("component.alert.contact.support.label") })
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  const el = document.getElementById("titleField")
  el?.focus()
})
</script>

<style lang="postcss">
.ant-modal.create-qr-modal {
  @apply max-md:max-w-[calc(100%_-_1rem)] max-w-[28rem];
  .ant-modal-content {
    @apply rounded-lg bg-white p-4 pt-8;

    .ant-modal-header {
      @apply mb-2;
      .ant-modal-title {
        @apply text-black text-xl font-semibold leading-normal;
      }
    }

    .ant-modal-body {
      .ant-form {
        label {
          @apply text-black/60 text-xs font-medium leading-normal;
        }
      }
    }

    .ant-modal-footer {
      @apply mt-10;
    }
  }
}
</style>
